import { useRef, useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Search,
  Upload,
} from 'lucide-react';
import { InfoButton } from '../../components/InfoModal';
import { compareSsCode, sortSsCodeStrings } from '../../utils/ssCodeSort';

const COUNCIL_REGISTER_URL =
  'https://datastudio.google.com/u/0/reporting/c4b7dd55-b485-4481-9854-2eafff81a4dd/page/u5aZF';

const PAGE_SIZE = 50;

const CSV_TEMPLATE = `person_name,company_name,email,phone,ss_code,region
Jane Smith,Fireco,info@fireco.co.nz,0800 101 232,SS 15.2,Auckland
Jane Smith,Fireco,info@fireco.co.nz,0800 101 232,SS 15.4,Auckland
`;

function downloadCsvTemplate() {
  const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'iqp-register-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function formatExpiry(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-NZ');
  } catch {
    return iso;
  }
}

function IqpTable({ rows, compact = false }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-card">
      <table className={`min-w-full divide-y divide-gray-200 ${compact ? 'text-xs' : 'text-sm'}`}>
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap">IQP name</th>
            <th className="px-3 py-2 text-left font-medium text-gray-500">Company</th>
            <th className="px-3 py-2 text-left font-medium text-gray-500">SS</th>
            <th className="px-3 py-2 text-left font-medium text-gray-500">Mobile</th>
            <th className="px-3 py-2 text-left font-medium text-gray-500">Email</th>
            <th className="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap">IQP #</th>
            <th className="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap">Expiry</th>
            <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.id || `${row.person_name}-${row.ss_code}-${row.company_name}`} className="hover:bg-gray-50">
              <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{row.person_name}</td>
              <td className="px-3 py-2 text-gray-600 max-w-[180px] truncate" title={row.company_name || ''}>
                {row.company_name || '—'}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                <span className="px-1.5 py-0.5 bg-primary-50 text-primary-800 text-xs rounded">{row.ss_code}</span>
              </td>
              <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{row.phone || '—'}</td>
              <td className="px-3 py-2 max-w-[200px] truncate">
                {row.email ? (
                  <a href={`mailto:${row.email}`} className="text-primary-700 hover:underline">
                    {row.email}
                  </a>
                ) : (
                  '—'
                )}
              </td>
              <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{row.registration_number || '—'}</td>
              <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatExpiry(row.registration_expiry)}</td>
              <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{row.council_status || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaginationBar({ page, total, pageSize, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm text-gray-600">
      <span>
          {from}–{to} of {total.toLocaleString()}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="p-2 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="min-w-[5rem] text-center">
          {page} / {totalPages}
        </span>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className="p-2 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function IqpTab({ onHeaderActions } = {}) {
  const queryClient = useQueryClient();
  const fileRef = useRef(null);
  const [search, setSearch] = useState('');
  const [ssFilter, setSsFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [groupMode, setGroupMode] = useState('all');
  const [page, setPage] = useState(1);
  const [importing, setImporting] = useState(false);

  const { data: meta } = useQuery({
    queryKey: ['iqp-register', 'meta'],
    queryFn: async () => (await api.get('/iqp-register/meta')).data,
  });

  const { data: listData, isLoading, error } = useQuery({
    queryKey: ['iqp-register', { search, ssFilter, companyFilter }],
    queryFn: async () => {
      const res = await api.get('/iqp-register', {
        params: {
          search: search || undefined,
          ssCode: ssFilter || undefined,
          company: companyFilter || undefined,
        },
      });
      return res.data?.data ?? [];
    },
    enabled: groupMode === 'all',
  });

  const { data: groupedSs, isLoading: ssLoading } = useQuery({
    queryKey: ['iqp-register', 'grouped-by-ss'],
    queryFn: async () => (await api.get('/iqp-register/grouped-by-ss')).data?.data ?? { bySs: {}, ssList: [] },
    enabled: groupMode === 'bySs',
  });

  const { data: groupedCompany, isLoading: companyLoading } = useQuery({
    queryKey: ['iqp-register', 'grouped-by-company'],
    queryFn: async () =>
      (await api.get('/iqp-register/grouped-by-company')).data?.data ?? { byCompany: {}, companyList: [] },
    enabled: groupMode === 'byCompany',
  });

  const importMutation = useMutation({
    mutationFn: async (csv) => api.post('/iqp-register/import', { csv, updateExisting: true }),
    onSuccess: (res) => {
      const r = res.data;
      const extra =
        r.format === 'council_mobile'
          ? ` (${r.sourceRows} council rows ??${r.expandedRows} IQP+SS entries)`
          : '';
      toast.success(`Import: ${r.inserted} new, ${r.updated} updated${extra}`);
      queryClient.invalidateQueries({ queryKey: ['iqp-register'] });
      queryClient.invalidateQueries({ queryKey: ['service-network'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Import failed'),
    onSettled: () => setImporting(false),
  });

  const handleCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const reader = new FileReader();
    reader.onload = () => importMutation.mutate(String(reader.result || ''));
    reader.onerror = () => {
      toast.error('Could not read file');
      setImporting(false);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const rows = Array.isArray(listData) ? listData : [];
  const bySs = groupedSs?.bySs ?? {};
  const byCompany = groupedCompany?.byCompany ?? {};
  const ssOptions = sortSsCodeStrings(meta?.ssList ?? []);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, page]);

  const handleSearchChange = (v) => {
    setSearch(v);
    setPage(1);
  };
  const handleSsChange = (v) => {
    setSsFilter(v);
    setPage(1);
  };
  const handleCompanyChange = (v) => {
    setCompanyFilter(v);
    setPage(1);
  };

  useEffect(() => {
    if (!onHeaderActions) return undefined;
    onHeaderActions(
      <>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={importing}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-white text-navy-900 hover:bg-white/90 disabled:opacity-50"
        >
          <Upload className="w-4 h-4" />
          {importing ? 'Importing…' : 'Import CSV'}
        </button>
        <button
          type="button"
          onClick={downloadCsvTemplate}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-white/30 text-white hover:bg-white/10"
        >
          <Download className="w-4 h-4" />
          CSV template
        </button>
        <a
          href={COUNCIL_REGISTER_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-sky-200 hover:text-white"
        >
          <ExternalLink className="w-4 h-4" />
          Council
        </a>
      </>
    );
    return () => onHeaderActions(null);
  }, [onHeaderActions, importing]);

  return (
    <div>
      <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleCsvFile} />
      <div className="mb-4 p-4 bg-sky-50 border border-sky-200 rounded-lg text-sm text-sky-950">
        <strong>Auckland Council mobile CSV</strong> — in Looker Studio, right-click the chart —{' '}
        <strong>Export data</strong>, then Import that file here. Seed file:{' '}
        <code className="text-xs bg-white px-1 rounded">database_seeds/auckland_council_iqp_register.csv</code>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <InfoButton title="Registered IQP list">
          <p>
            Table list with pagination ??best for large Council registers. Use search and SS / company filters to narrow
            results. Import CSV from the page header.
          </p>
        </InfoButton>
        <p className="text-xs text-gray-500">Import / template / Council link are in the header.</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 border border-gray-300 rounded-lg p-1 bg-gray-50 shrink-0">
            {[
              ['all', 'All'],
              ['bySs', 'By SS'],
              ['byCompany', 'By company'],
            ].map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                onClick={() => {
                  setGroupMode(mode);
                  setPage(1);
                }}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  groupMode === mode ? 'bg-white shadow text-primary-700' : 'text-gray-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {groupMode === 'all' && (
          <>
            <div className="flex-1 relative min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search name, company, SS…"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <select
              value={ssFilter}
              onChange={(e) => handleSsChange(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="">All SS</option>
              {ssOptions.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={companyFilter}
              onChange={(e) => handleCompanyChange(e.target.value)}
              placeholder="Company"
              className="px-4 py-2 border rounded-lg min-w-[140px]"
            />
          </>
        )}
      </div>

      {error && <p className="text-red-600 mb-4">Error: {error.message}</p>}

      {groupMode === 'all' &&
        (isLoading ? (
          <p className="text-center py-8 text-gray-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-center py-8 text-gray-500 text-sm">
            No registered IQPs yet. Import Council CSV or upload Form 12A on the Agent tab.
          </p>
        ) : (
          <>
            <IqpTable rows={pagedRows} />
            <PaginationBar page={page} total={rows.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
          </>
        ))}

      {groupMode === 'bySs' &&
        (ssLoading ? (
          <p className="text-center py-8 text-gray-500">Loading…</p>
        ) : (
          <div className="space-y-6">
            {Object.entries(bySs)
              .sort(([a], [b]) => compareSsCode(a, b))
              .map(([ssCode, list]) => (
              <div key={ssCode} className="bg-card rounded-lg border overflow-hidden">
                <div className="px-4 py-3 bg-navy-900 border-b flex justify-between items-center text-white">
                  <div>
                    <h3 className="font-semibold">{ssCode}</h3>
                    <p className="text-sm text-white/70">{list.length} IQP(s)</p>
                  </div>
                </div>
                <IqpTable rows={list} compact />
              </div>
            ))}
          </div>
        ))}

      {groupMode === 'byCompany' &&
        (companyLoading ? (
          <p className="text-center py-8 text-gray-500">Loading…</p>
        ) : (
          <div className="space-y-6">
            {Object.entries(byCompany).map(([company, list]) => (
              <div key={company} className="bg-card rounded-lg border overflow-hidden">
                <div className="px-4 py-3 bg-navy-900 border-b text-white">
                  <h3 className="font-semibold">{company}</h3>
                  <p className="text-sm text-white/70">{list.length} registration(s)</p>
                </div>
                <IqpTable rows={list} compact />
              </div>
            ))}
          </div>
        ))}
    </div>
  );
}
